import React, { useState, useEffect } from 'react';
import fetchData from '../../../api/fetchData';
import { useTranslation } from 'react-i18next';
import { supportedLngs } from '../../../i18n';

function AdminSpecialOfferEditor({ onContentUpdated }) {
    const { i18n } = useTranslation();
    const [allTranslations, setAllTranslations] = useState(null);
    const [editingLanguage, setEditingLanguage] = useState(i18n.language.split('-')[0] || 'ru');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });

    // Настройки блока
    const [settings, setSettings] = useState({
        is_active: true,
        coupons_left: 3,
        discount_percent: 30,
        original_price: 1500,
        timer_hours: 24,
        activate_timer: false
    });

    // Переводы для текущего языка
    const [currentTranslations, setCurrentTranslations] = useState({
        badge: '',
        couponsLeft: '',
        title: '',
        timeLeft: '',
        serviceTitle: '',
        features: '',
        getCoupon: '',
        formTitle: '',
        namePlaceholder: '',
        emailPlaceholder: '',
        phonePlaceholder: '',
        detailsTitle: '',
        detailsText: '',
        conditionsTitle: '',
        conditions: '',
        showDetails: '',
        hideDetails: '',
        landingPage: '',
        website: '',
        ecommerce: '',
        submitting: '',
        submit: '',
        successMessage: '',
        errorMessage: '',
        close: ''
    });

    useEffect(() => {
        const loadSpecialOfferData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetchData('/admin/special_offer/admin_get_special_offer_data.php', 'GET', null, { credentials: 'include' });
                if (response && response.status === 'success' && response.item) {
                    setSettings({
                        is_active: response.item.is_active === 1,
                        coupons_left: parseInt(response.item.coupons_left) || '',
                        discount_percent: parseInt(response.item.discount_percent) || '',
                        original_price: parseFloat(response.item.original_price) || '',
                        timer_hours: parseInt(response.item.timer_hours) || ''
                    });
                    const translations = response.item.translations || {};

                    // Инициализируем переводы для всех языков, если их нет
                    const defaultTranslations = {
                        ru: {
                            badge: 'ОГРАНИЧЕННОЕ ПРЕДЛОЖЕНИЕ',
                            title: 'Получите 25% скидку на создание лендинга!',
                            description: 'Создайте современный лендинг с Next.js и TailwindCSS. Полный пакет "под ключ" - от идеи до запуска!',
                            buttonText: 'Получить скидку',
                            formTitle: 'Заполните форму для получения скидки',
                            serviceLabel: 'Выберите услугу',
                            couponsLeft: 'купонов осталось',
                            timerLabel: 'До окончания акции:',
                            landingPage: 'Лендинг',
                            website: 'Сайт',
                            ecommerce: 'Интернет-магазин',
                            submitting: 'Отправка...',
                            submit: 'Получить скидку',
                            successMessage: 'Спасибо! Ваш купон отправлен на почту.',
                            errorMessage: 'Произошла ошибка. Попробуйте еще раз.',
                            close: 'Закрыть',
                            serviceTitle: 'Что входит в услугу:',
                            features: 'Прототип и структура страницы\nСовременный UI с адаптивным дизайном\nРазработка на Next.js + TailwindCSS\nФорма обратной связи\nSEO-оптимизация\nПодключение аналитики\nДеплой и запуск\n1 месяц технической поддержки',
                            getCoupon: 'Получить купон',
                            namePlaceholder: 'Ваше имя',
                            emailPlaceholder: 'Email адрес',
                            phonePlaceholder: 'Номер телефона',
                            detailsTitle: 'Подробно об акции',
                            detailsText: '📌 Что входит в услугу:\n\n• Подготовка структуры страницы и прототип (wireframe)\n• Современный, удобный дизайн (адаптивный для телефона и компьютера)\n• Создание сайта с использованием Next.js + TailwindCSS (скорость, стабильность, SEO-дружелюбность)\n• Контактная форма для получения заявок\n• Базовая SEO-оптимизация (чтобы сайт находили в Google)\n• Подключение Google Analytics (для отслеживания посетителей)\n• Загрузка сайта на сервер и запуск\n• 1 месяц технической поддержки после запуска\n\n⏱ Длительность проекта:\n\n2–4 недели (зависит от количества контента и дополнительных пожеланий).\n\n🛠 Используемые технологии:\n\nNext.js (современная React система)\nTailwindCSS (для более быстрого и чистого дизайна)\nVercel хостинг (быстрая загрузка и надежность)\n\n💡 Возможности на будущее:\n\nСайт можно расширить: добавить блог, интернет-магазин или дополнительные языки.\nВозможны различные интеграции: Google Ads, Facebook Pixel, CRM системы.\nВозможность адаптировать под специфические бизнес-потребности (управление заказами, автоматизированные рассылки, платежная система).',
                            conditionsTitle: 'Условия акции:',
                            conditions: 'Скидка: 25% от базовой стоимости пакета «Лендинг под ключ»\nБазовая цена: 600€ → по купону: 450€\nСрок действия: до 30 сентября 2025 включительно\nКоличество предложений: всего 3 места (после исчерпания акция закрывается)\nКупон: уникальный код придёт на e-mail после заявки',
                            showDetails: '↓ Подробнее об акции',
                            hideDetails: '↑ Скрыть подробности'
                        },
                        lt: {
                            badge: 'RIBOTA PASIŪLYMAS',
                            title: 'Gaukite 25% nuolaidą lėdingo puslapio kūrimui!',
                            description: 'Sukurkite modernų lėdingo puslapį su Next.js ir TailwindCSS. Pilnas paketas "po raktą" - nuo idėjos iki paleidimo!',
                            buttonText: 'Gauti nuolaidą',
                            formTitle: 'Užpildykite formą nuolaidai gauti',
                            serviceLabel: 'Pasirinkite paslaugą',
                            couponsLeft: 'kuponų liko',
                            timerLabel: 'Iki akcijos pabaigos:',
                            landingPage: 'Lėdingo puslapis',
                            website: 'Svetainė',
                            ecommerce: 'El. parduotuvė',
                            submitting: 'Siunčiama...',
                            submit: 'Gauti nuolaidą',
                            successMessage: 'Ačiū! Jūsų kuponas išsiųstas į el. paštą.',
                            errorMessage: 'Įvyko klaida. Bandykite dar kartą.',
                            close: 'Uždaryti',
                            serviceTitle: 'Kas įeina į paslaugą:',
                            features: 'Puslapio struktūros parengimas ir prototipas\nModernus, patogus naudoti dizainas (veikiantis telefone ir kompiuteryje)\nSvetainės sukūrimas su naujausiomis technologijomis (Next.js + TailwindCSS)\nKontaktų forma užklausoms gauti\nPagrindinis SEO sutvarkymas (kad svetainę rastų Google)\nGoogle Analytics prijungimas (lankytojams sekti)\nSvetainės įkėlimas į serverį ir paleidimas\n1 mėnesio techninė pagalba',
                            getCoupon: 'Gauti kuponą',
                            namePlaceholder: 'Jūsų vardas',
                            emailPlaceholder: 'El. pašto adresas',
                            phonePlaceholder: 'Telefono numeris',
                            detailsTitle: 'Išsamiai apie akciją',
                            detailsText: '📌 Kas įeina į paslaugą:\n\n• Puslapio struktūros parengimas ir prototipas (wireframe)\n• Modernus, patogus naudoti dizainas (prisitaikantis prie telefono ir kompiuterio)\n• Svetainės sukūrimas naudojant Next.js + TailwindCSS (greitis, stabilumas, SEO draugiškumas)\n• Kontaktų forma užklausoms gauti\n• Pagrindinis SEO sutvarkymas (kad svetainę rastų Google)\n• Google Analytics prijungimas (lankytojų stebėjimui)\n• Svetainės įkėlimas į serverį ir paleidimas\n• 1 mėnesio techninė pagalba po paleidimo\n\n⏱ Projekto trukmė:\n\n2–4 savaitės (priklauso nuo turinio kiekio ir papildomų pageidavimų).\n\n🛠 Naudojamos technologijos:\n\nNext.js (moderni React sistema)\nTailwindCSS (greitesniam ir švaresniam dizainui)\nVercel hostingas (greitas užkrovimas ir patikimumas)\n\n💡 Galimybės ateičiai:\n\nSvetainę galima plėsti: pridėti tinklaraštį, e. parduotuvę ar papildomas kalbas.\nGalimos įvairios integracijos: Google Ads, Facebook Pixel, CRM sistemos.\nGalimybė pritaikyti pagal specifinius verslo poreikius (užsakymų valdymas, automatizuoti laiškai, mokėjimų sistema).',
                            conditionsTitle: 'Akcijos sąlygos:',
                            conditions: 'Nuolaida: 25% nuo bazinio paketo "Lėdingo puslapis po raktą" kainos\nBazinė kaina: 600€ → su kuponu: 450€\nGaliojimo laikas: iki 2025 m. rugsėjo 30 d. imtinai\nPasiūlymų skaičius: tik 3 vietos (išsekus akcija užsidaro)\nKuponas: unikalus kodas bus išsiųstas el. paštu po užklausos',
                            showDetails: '↓ Išsamiau apie akciją',
                            hideDetails: '↑ Slėpti detales'
                        },
                        en: {
                            badge: 'LIMITED OFFER',
                            title: 'Get 25% discount on landing page creation!',
                            description: 'Create a modern landing page with Next.js and TailwindCSS. Complete "turnkey" package - from idea to launch!',
                            buttonText: 'Get Discount',
                            formTitle: 'Fill out the form to get a discount',
                            serviceLabel: 'Select a service',
                            couponsLeft: 'coupons left',
                            timerLabel: 'Until the end of the promotion:',
                            landingPage: 'Landing Page',
                            website: 'Website',
                            ecommerce: 'E-commerce',
                            submitting: 'Submitting...',
                            submit: 'Get Discount',
                            successMessage: 'Thank you! Your coupon has been sent to your email.',
                            errorMessage: 'An error occurred. Please try again.',
                            close: 'Close',
                            serviceTitle: 'What is included in the service:',
                            features: 'Prototype and page structure\nModern UI with responsive design\nNext.js + TailwindCSS development\nContact form\nSEO optimization\nAnalytics integration\nDeployment and launch\n1 month technical support',
                            getCoupon: 'Get Coupon',
                            namePlaceholder: 'Your Name',
                            emailPlaceholder: 'Email Address',
                            phonePlaceholder: 'Phone Number',
                            detailsTitle: 'Details about the offer',
                            detailsText: '📌 What is included in the service:\n\n• Page structure preparation and prototype (wireframe)\n• Modern, user-friendly design (responsive for phone and computer)\n• Website creation using Next.js + TailwindCSS (speed, stability, SEO-friendly)\n• Contact form for receiving inquiries\n• Basic SEO optimization (so the site can be found on Google)\n• Google Analytics integration (for visitor tracking)\n• Website upload to server and launch\n• 1 month technical support after launch\n\n⏱ Project duration:\n\n2–4 weeks (depends on content amount and additional requests).\n\n🛠 Technologies used:\n\nNext.js (modern React system)\nTailwindCSS (for faster and cleaner design)\nVercel hosting (fast loading and reliability)\n\n💡 Future possibilities:\n\nThe website can be expanded: add a blog, e-commerce or additional languages.\nVarious integrations are possible: Google Ads, Facebook Pixel, CRM systems.\nPossibility to adapt to specific business needs (order management, automated newsletters, payment system).',
                            conditionsTitle: 'Offer conditions:',
                            conditions: 'Discount: 25% off the base price of "Landing Page Turnkey" package\nBase price: 600€ → with coupon: 450€\nValid until: September 30, 2025 inclusive\nNumber of offers: only 3 spots (offer closes when exhausted)\nCoupon: unique code will be sent to email after request',
                            showDetails: '↓ More details about the offer',
                            hideDetails: '↑ Hide details'
                        },
                        de: {
                            badge: 'BEGRENZTES ANGEBOT',
                            title: 'Erhalten Sie 25% Rabatt auf die Erstellung einer Landing Page!',
                            description: 'Erstellen Sie eine moderne Landing Page mit Next.js und TailwindCSS. Komplettes "schlüsselfertiges" Paket - von der Idee bis zum Start!',
                            buttonText: 'Rabatt erhalten',
                            formTitle: 'Füllen Sie das Formular aus, um einen Rabatt zu erhalten',
                            serviceLabel: 'Wählen Sie einen Service',
                            couponsLeft: 'Gutscheine übrig',
                            timerLabel: 'Bis zum Ende der Aktion:',
                            landingPage: 'Landing Page',
                            website: 'Webseite',
                            ecommerce: 'E-Commerce',
                            submitting: 'Wird gesendet...',
                            submit: 'Rabatt erhalten',
                            successMessage: 'Vielen Dank! Ihr Gutschein wurde an Ihre E-Mail gesendet.',
                            errorMessage: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
                            close: 'Schließen',
                            serviceTitle: 'Was ist im Service enthalten:',
                            features: 'Prototyp und Seitenstruktur\nModernes UI mit responsivem Design\nNext.js + TailwindCSS Entwicklung\nKontaktformular\nSEO-Optimierung\nAnalytics-Integration\nDeployment und Start\n1 Monat technischer Support',
                            getCoupon: 'Gutschein erhalten',
                            namePlaceholder: 'Ihr Name',
                            emailPlaceholder: 'E-Mail-Adresse',
                            phonePlaceholder: 'Telefonnummer',
                            detailsTitle: 'Details zum Angebot',
                            detailsText: '📌 Was ist im Service enthalten:\n\n• Vorbereitung der Seitenstruktur und Prototyp (Wireframe)\n• Modernes, benutzerfreundliches Design (responsive für Telefon und Computer)\n• Website-Erstellung mit Next.js + TailwindCSS (Geschwindigkeit, Stabilität, SEO-freundlich)\n• Kontaktformular für Anfragen\n• Grundlegende SEO-Optimierung (damit die Website bei Google gefunden wird)\n• Google Analytics Integration (für Besucherverfolgung)\n• Website-Upload auf Server und Start\n• 1 Monat technischer Support nach dem Start\n\n⏱ Projektdauer:\n\n2–4 Wochen (abhängig von der Inhaltsmenge und zusätzlichen Wünschen).\n\n🛠 Verwendete Technologien:\n\nNext.js (modernes React-System)\nTailwindCSS (für schnelleres und sauberes Design)\nVercel Hosting (schnelles Laden und Zuverlässigkeit)\n\n💡 Zukünftige Möglichkeiten:\n\nDie Website kann erweitert werden: Blog, E-Commerce oder zusätzliche Sprachen hinzufügen.\nVerschiedene Integrationen sind möglich: Google Ads, Facebook Pixel, CRM-Systeme.\nMöglichkeit zur Anpassung an spezifische Geschäftsanforderungen (Bestellverwaltung, automatisierte Newsletter, Zahlungssystem).',
                            conditionsTitle: 'Angebotsbedingungen:',
                            conditions: 'Rabatt: 25% vom Grundpreis des "Landing Page Schlüsselfertig" Pakets\nGrundpreis: 600€ → mit Gutschein: 450€\nGültig bis: 30. September 2025 einschließlich\nAnzahl der Angebote: nur 3 Plätze (Angebot schließt bei Erschöpfung)\nGutschein: eindeutiger Code wird nach Anfrage per E-Mail gesendet',
                            showDetails: '↓ Mehr Details zum Angebot',
                            hideDetails: '↑ Details ausblenden'
                        }
                    };

                    // Объединяем существующие переводы с дефолтными
                    const mergedTranslations = {};
                    ['ru', 'lt', 'en', 'de'].forEach(lang => {
                        mergedTranslations[lang] = {
                            ...defaultTranslations[lang] || {},
                            ...translations[lang] || {}
                        };
                    });

                    setAllTranslations(mergedTranslations);
                } else {
                    setError(response?.message || 'Не удалось загрузить данные блока скидки.');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadSpecialOfferData();
    }, []);

    useEffect(() => {
        if (allTranslations) {
            const langData = allTranslations[editingLanguage] || {};
            setCurrentTranslations(prev => ({
                ...prev,
                ...langData
            }));
        }
    }, [editingLanguage, allTranslations]);

    const handleLanguageTabClick = (langCode) => {
        setEditingLanguage(langCode);
    };

    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value === '' ? '' : value
        }));
    };

    const handleTranslationChange = (field, value) => {
        setCurrentTranslations(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        console.log('🔄 Начинаем сохранение...');
        console.log('📝 Текущие переводы:', currentTranslations);
        console.log('🌐 Язык:', editingLanguage);
        setIsSaving(true);
        setSaveStatus({ message: '', type: '' });

        try {
            // Обновляем переводы для текущего языка
            const updatedTranslations = {
                ...allTranslations,
                [editingLanguage]: currentTranslations
            };

            const saveData = {
                is_active: settings.is_active,
                coupons_left: settings.coupons_left === '' ? 0 : settings.coupons_left,
                discount_percent: settings.discount_percent === '' ? 0 : settings.discount_percent,
                original_price: settings.original_price === '' ? 0 : settings.original_price,
                timer_hours: settings.timer_hours === '' ? 0 : settings.timer_hours,
                activate_timer: settings.activate_timer,
                translations: updatedTranslations
            };

            console.log('📤 Отправляем данные:', saveData);
            const response = await fetchData('/admin/special_offer/admin_save_special_offer_data.php', 'POST', saveData, { credentials: 'include' });
            console.log('📥 Получен ответ:', response);

            if (response && response.status === 'success') {
                setSaveStatus({ message: 'Настройки блока скидки успешно сохранены!', type: 'success' });
                setAllTranslations(updatedTranslations);
                if (onContentUpdated) {
                    onContentUpdated();
                }
                setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
            } else {
                setSaveStatus({ message: response?.message || 'Ошибка при сохранении', type: 'error' });
            }
        } catch (err) {
            console.error('❌ Ошибка при сохранении:', err);
            setSaveStatus({ message: 'Ошибка при сохранении: ' + err.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const calculateDiscountPrice = () => {
        const price = settings.original_price === '' ? 0 : settings.original_price;
        const discount = settings.discount_percent === '' ? 0 : settings.discount_percent;
        return Math.round(price * (1 - discount / 100));
    };

    const calculateSavings = () => {
        const price = settings.original_price === '' ? 0 : settings.original_price;
        return price - calculateDiscountPrice();
    };

    if (loading) {
        return <div className="admin-loading">Загрузка данных блока скидки...</div>;
    }

    if (error) {
        return <div className="admin-error">Ошибка: {error}</div>;
    }

    return (
        <div className="admin-special-offer-editor">
            <h2>Редактирование блока скидки</h2>

            {/* Настройки блока */}
            <div className="admin-section">
                <h3>Основные настройки</h3>

                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={settings.is_active}
                            onChange={(e) => handleSettingsChange('is_active', e.target.checked)}
                        />
                        Блок скидки активен
                    </label>
                </div>

                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={settings.activate_timer || false}
                            onChange={(e) => handleSettingsChange('activate_timer', e.target.checked)}
                        />
                        Активировать таймер (запустить отсчет времени)
                    </label>
                    <small className="help-text">
                        Внимание! После активации таймер нельзя будет остановить.
                        Он будет работать указанное количество часов с момента активации.
                    </small>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Количество купонов:</label>
                        <input
                            type="number"
                            value={settings.coupons_left || ''}
                            onChange={(e) => handleSettingsChange('coupons_left', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            min="0"
                            max="100"
                            placeholder="Введите количество"
                        />
                    </div>

                    <div className="form-group">
                        <label>Процент скидки:</label>
                        <input
                            type="number"
                            value={settings.discount_percent || ''}
                            onChange={(e) => handleSettingsChange('discount_percent', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            min="1"
                            max="99"
                            placeholder="Введите процент"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Оригинальная цена (€):</label>
                        <input
                            type="number"
                            value={settings.original_price || ''}
                            onChange={(e) => handleSettingsChange('original_price', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            min="1"
                            step="0.01"
                            placeholder="Введите цену"
                        />
                    </div>

                    <div className="form-group">
                        <label>Таймер (часы):</label>
                        <input
                            type="number"
                            value={settings.timer_hours || ''}
                            onChange={(e) => handleSettingsChange('timer_hours', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            onFocus={(e) => e.target.select()}
                            min="1"
                            max="168"
                            placeholder="Введите часы"
                        />
                    </div>
                </div>

                <div className="price-preview">
                    <h4>Предварительный просмотр цен:</h4>
                    <p>Оригинальная цена: {settings.original_price === '' ? '0' : settings.original_price}€</p>
                    <p>Цена со скидкой: {calculateDiscountPrice()}€</p>
                    <p>Экономия: {calculateSavings()}€</p>
                </div>
            </div>

            {/* Переводы */}
            <div className="admin-section">
                <h3>Переводы</h3>

                <div className="language-tabs">
                    {supportedLngs.map(lang => (
                        <button
                            key={lang}
                            className={`language-tab ${editingLanguage === lang ? 'active' : ''}`}
                            onClick={() => handleLanguageTabClick(lang)}
                        >
                            {lang.toUpperCase()}
                        </button>
                    ))}
                </div>

                <div className="translations-form">
                    <div className="form-group">
                        <label>Бейдж (ограниченное предложение):</label>
                        <input
                            type="text"
                            value={currentTranslations.badge || ''}
                            onChange={(e) => handleTranslationChange('badge', e.target.value)}
                            placeholder="Ограниченное предложение!"
                        />
                    </div>

                    <div className="form-group">
                        <label>Заголовок:</label>
                        <input
                            type="text"
                            value={currentTranslations.title || ''}
                            onChange={(e) => handleTranslationChange('title', e.target.value)}
                            placeholder="Получите скидку 30% на лендинг-пейдж!"
                        />
                    </div>

                    <div className="form-group">
                        <label>Купонов осталось:</label>
                        <input
                            type="text"
                            value={currentTranslations.couponsLeft || ''}
                            onChange={(e) => handleTranslationChange('couponsLeft', e.target.value)}
                            placeholder="купонов осталось"
                        />
                    </div>

                    <div className="form-group">
                        <label>До окончания акции:</label>
                        <input
                            type="text"
                            value={currentTranslations.timeLeft || ''}
                            onChange={(e) => handleTranslationChange('timeLeft', e.target.value)}
                            placeholder="До окончания акции:"
                        />
                    </div>

                    <div className="form-group">
                        <label>Заголовок услуг:</label>
                        <input
                            type="text"
                            value={currentTranslations.serviceTitle || ''}
                            onChange={(e) => handleTranslationChange('serviceTitle', e.target.value)}
                            placeholder="Что входит в услугу:"
                        />
                    </div>

                    <div className="features-section">
                        <h4>Особенности услуги:</h4>
                        <div className="form-group">
                            <label>Список особенностей (каждая с новой строки):</label>
                            <textarea
                                value={currentTranslations.features || ''}
                                onChange={(e) => handleTranslationChange('features', e.target.value)}
                                placeholder="Введите особенности услуги, каждая с новой строки"
                                rows="8"
                                style={{ width: '100%', minHeight: '150px' }}
                            />
                            <small className="help-text">
                                Каждая особенность должна быть на отдельной строке.
                                Например:<br />
                                • Прототип и структура страницы<br />
                                • Современный UI с адаптивным дизайном<br />
                                • Разработка на Next.js + TailwindCSS
                            </small>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Заголовок услуг:</label>
                        <input
                            type="text"
                            value={currentTranslations.serviceTitle || ''}
                            onChange={(e) => handleTranslationChange('serviceTitle', e.target.value)}
                            placeholder="Что входит в услугу:"
                        />
                    </div>

                    <div className="form-group">
                        <label>Кнопка "Получить купон":</label>
                        <input
                            type="text"
                            value={currentTranslations.getCoupon || ''}
                            onChange={(e) => handleTranslationChange('getCoupon', e.target.value)}
                            placeholder="Получить купон"
                        />
                    </div>

                    <div className="form-group">
                        <label>Заголовок формы:</label>
                        <input
                            type="text"
                            value={currentTranslations.formTitle || ''}
                            onChange={(e) => handleTranslationChange('formTitle', e.target.value)}
                            placeholder="Заполните форму для получения купона"
                        />
                    </div>

                    <div className="form-placeholders">
                        <h4>Плейсхолдеры формы:</h4>
                        <div className="form-group">
                            <label>Имя:</label>
                            <input
                                type="text"
                                value={currentTranslations.namePlaceholder || ''}
                                onChange={(e) => handleTranslationChange('namePlaceholder', e.target.value)}
                                placeholder="Ваше имя"
                            />
                        </div>
                        <div className="form-group">
                            <label>Email:</label>
                            <input
                                type="text"
                                value={currentTranslations.emailPlaceholder || ''}
                                onChange={(e) => handleTranslationChange('emailPlaceholder', e.target.value)}
                                placeholder="Email адрес"
                            />
                        </div>
                        <div className="form-group">
                            <label>Телефон:</label>
                            <input
                                type="text"
                                value={currentTranslations.phonePlaceholder || ''}
                                onChange={(e) => handleTranslationChange('phonePlaceholder', e.target.value)}
                                placeholder="Номер телефона"
                            />
                        </div>
                    </div>

                    <div className="details-section">
                        <h4>Блок "Подробно":</h4>
                        <div className="form-group">
                            <label>Заголовок блока:</label>
                            <input
                                type="text"
                                value={currentTranslations.detailsTitle || ''}
                                onChange={(e) => handleTranslationChange('detailsTitle', e.target.value)}
                                placeholder="Подробно об акции"
                            />
                        </div>
                        <div className="form-group">
                            <label>Описание акции:</label>
                            <textarea
                                value={currentTranslations.detailsText || ''}
                                onChange={(e) => handleTranslationChange('detailsText', e.target.value)}
                                placeholder="Подробное описание акции"
                            ></textarea>
                        </div>
                        <div className="form-group">
                            <label>Заголовок условий:</label>
                            <input
                                type="text"
                                value={currentTranslations.conditionsTitle || ''}
                                onChange={(e) => handleTranslationChange('conditionsTitle', e.target.value)}
                                placeholder="Условия акции:"
                            />
                        </div>
                        <div className="form-group">
                            <label>Список условий (каждое с новой строки):</label>
                            <textarea
                                value={currentTranslations.conditions || ''}
                                onChange={(e) => handleTranslationChange('conditions', e.target.value)}
                                placeholder="Введите условия акции, каждое с новой строки"
                                rows="6"
                                style={{ width: '100%', minHeight: '120px' }}
                            />
                            <small className="help-text">
                                Каждое условие должно быть на отдельной строке.
                                Например:<br />
                                • Скидка: 25% от базовой стоимости<br />
                                • Базовая цена: 600€ → по купону: 450€<br />
                                • Срок действия: до 30 сентября 2025
                            </small>
                        </div>
                        <div className="form-group">
                            <label>Кнопка "Показать подробности":</label>
                            <input
                                type="text"
                                value={currentTranslations.showDetails || ''}
                                onChange={(e) => handleTranslationChange('showDetails', e.target.value)}
                                placeholder="↓ Подробнее об акции"
                            />
                        </div>
                        <div className="form-group">
                            <label>Кнопка "Скрыть подробности":</label>
                            <input
                                type="text"
                                value={currentTranslations.hideDetails || ''}
                                onChange={(e) => handleTranslationChange('hideDetails', e.target.value)}
                                placeholder="↑ Скрыть подробности"
                            />
                        </div>
                    </div>

                    <div className="service-options">
                        <h4>Варианты услуг:</h4>
                        <div className="form-group">
                            <label>Лендинг-пейдж:</label>
                            <input
                                type="text"
                                value={currentTranslations.landingPage || ''}
                                onChange={(e) => handleTranslationChange('landingPage', e.target.value)}
                                placeholder="Лендинг-пейдж"
                            />
                        </div>
                        <div className="form-group">
                            <label>Корпоративный сайт:</label>
                            <input
                                type="text"
                                value={currentTranslations.website || ''}
                                onChange={(e) => handleTranslationChange('website', e.target.value)}
                                placeholder="Корпоративный сайт"
                            />
                        </div>
                        <div className="form-group">
                            <label>Интернет-магазин:</label>
                            <input
                                type="text"
                                value={currentTranslations.ecommerce || ''}
                                onChange={(e) => handleTranslationChange('ecommerce', e.target.value)}
                                placeholder="Интернет-магазин"
                            />
                        </div>
                    </div>

                    <div className="messages-section">
                        <h4>Сообщения:</h4>
                        <div className="form-group">
                            <label>Отправляем:</label>
                            <input
                                type="text"
                                value={currentTranslations.submitting || ''}
                                onChange={(e) => handleTranslationChange('submitting', e.target.value)}
                                placeholder="Отправляем..."
                            />
                        </div>
                        <div className="form-group">
                            <label>Получить купон (кнопка):</label>
                            <input
                                type="text"
                                value={currentTranslations.submit || ''}
                                onChange={(e) => handleTranslationChange('submit', e.target.value)}
                                placeholder="Получить купон"
                            />
                        </div>
                        <div className="form-group">
                            <label>Успешное сообщение:</label>
                            <input
                                type="text"
                                value={currentTranslations.successMessage || ''}
                                onChange={(e) => handleTranslationChange('successMessage', e.target.value)}
                                placeholder="Купон отправлен на ваш email!"
                            />
                        </div>
                        <div className="form-group">
                            <label>Сообщение об ошибке:</label>
                            <input
                                type="text"
                                value={currentTranslations.errorMessage || ''}
                                onChange={(e) => handleTranslationChange('errorMessage', e.target.value)}
                                placeholder="Произошла ошибка. Попробуйте еще раз."
                            />
                        </div>
                        <div className="form-group">
                            <label>Закрыть:</label>
                            <input
                                type="text"
                                value={currentTranslations.close || ''}
                                onChange={(e) => handleTranslationChange('close', e.target.value)}
                                placeholder="Закрыть"
                            />
                        </div>

                    </div>
                </div>
            </div>

            {/* Кнопка сохранения */}
            <div className="admin-actions">
                <button
                    className="save-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? 'Сохранение...' : 'Сохранить изменения'}
                </button>

                {saveStatus.message && (
                    <div className={`save-status ${saveStatus.type}`}>
                        {saveStatus.message}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminSpecialOfferEditor;